import './insumo-form-modal.js';
import './fornecedor-form-modal.js';
import './movimentacao-form-modal.js';
import type { Insumo, Fornecedor, Movimentacao } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard } from '@pgcustom/design-system';
import type { EstoqueApi } from './estoque.api.js';
import type { PermissoesEstoque } from './estoque.permissoes.js';
import type { ErpInsumoFormModal } from './insumo-form-modal.js';
import type { ErpFornecedorFormModal } from './fornecedor-form-modal.js';
import type { ErpMovimentacaoFormModal } from './movimentacao-form-modal.js';

const COLUNAS_INSUMOS: PgTableColumn[] = [
  { key: 'nome', label: 'Insumo' },
  { key: 'quantidade', label: 'Quantidade', align: 'right' },
  { key: 'pontoReposicao', label: 'Ponto de reposição', align: 'right' },
  { key: 'status', label: 'Status' },
];

const COLUNAS_FORNECEDORES: PgTableColumn[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'email', label: 'E-mail' },
];

const COLUNAS_MOVIMENTACOES: PgTableColumn[] = [
  { key: 'insumo', label: 'Insumo' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'quantidade', label: 'Quantidade', align: 'right' },
  { key: 'descricao', label: 'Descrição' },
  { key: 'criadoEm', label: 'Data' },
];

/**
 * `<erp-estoque-page>` — cadastro de Insumos/Fornecedores e registro
 * de movimentações de estoque (Sprint 8, `ROADMAP.md`). Alerta de
 * baixo estoque é o dado real `abaixoDoPontoDeReposicao` (computado no
 * backend), mostrado como coluna de status — decremento automático por
 * etapa de OPS e notificação via `core.notifications` ficam fora desta
 * tela (DEC-030: sem fórmula de consumo/política de destinatário
 * documentada). Mesmo padrão de `<erp-financeiro-page>`.
 */
export class ErpEstoquePage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _estoqueApi: EstoqueApi | null = null;
  private _permissoes: PermissoesEstoque = { ler: false, criar: false, atualizar: false };

  private resultadoInsumos: Resultado<Insumo[]> = { status: 'carregando' };
  private resultadoFornecedores: Resultado<Fornecedor[]> = { status: 'carregando' };
  private resultadoMovimentacoes: Resultado<Movimentacao[]> = { status: 'carregando' };
  private insumosAtuais: Insumo[] = [];

  private modalInsumo!: ErpInsumoFormModal;
  private modalFornecedor!: ErpFornecedorFormModal;
  private modalMovimentacao!: ErpMovimentacaoFormModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set estoqueApi(valor: EstoqueApi) {
    this._estoqueApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesEstoque) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._estoqueApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); flex-wrap: wrap; }
        .acoes { display: flex; gap: var(--pg-space-3, 12px); flex-wrap: wrap; }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        h2 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-md, 1.125rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: var(--pg-space-6, 32px) 0 var(--pg-space-4, 16px); }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-6, 32px); }
      </style>
      <div class="topo">
        <div>
          <h1>Estoque</h1>
          <div class="subtitulo">Insumos, fornecedores e movimentações — alertas de baixo estoque em destaque</div>
        </div>
        <div class="acoes">
          ${this._permissoes.criar ? '<pg-button id="botao-movimentacao" variant="secondary">Registrar movimentação</pg-button>' : ''}
          ${this._permissoes.criar ? '<pg-button id="botao-novo-fornecedor" variant="secondary">Novo fornecedor</pg-button>' : ''}
          ${this._permissoes.criar ? '<pg-button id="botao-novo-insumo" variant="primary">Novo insumo</pg-button>' : ''}
        </div>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Insumos cadastrados" icone="estoque" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-baixo" label="Abaixo do ponto de reposição" icone="estoque" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-zerado" label="Sem estoque" icone="estoque" estado="carregando"></pg-kpi-card>
      </div>

      <pg-card padding="compacto">
        <div id="insumos"></div>
      </pg-card>

      <h2>Movimentações recentes</h2>
      <pg-card padding="compacto">
        <div id="movimentacoes"></div>
      </pg-card>

      <h2>Fornecedores</h2>
      <pg-card padding="compacto">
        <div id="fornecedores"></div>
      </pg-card>

      <erp-insumo-form-modal id="modal-insumo"></erp-insumo-form-modal>
      <erp-fornecedor-form-modal id="modal-fornecedor"></erp-fornecedor-form-modal>
      <erp-movimentacao-form-modal id="modal-movimentacao"></erp-movimentacao-form-modal>
    `;

    this.modalInsumo = this.root.querySelector('#modal-insumo') as ErpInsumoFormModal;
    if (this._estoqueApi) this.modalInsumo.estoqueApi = this._estoqueApi;
    this.modalInsumo.aoSalvar = () => void this.carregar();

    this.modalFornecedor = this.root.querySelector('#modal-fornecedor') as ErpFornecedorFormModal;
    if (this._estoqueApi) this.modalFornecedor.estoqueApi = this._estoqueApi;
    this.modalFornecedor.aoSalvar = () => void this.carregar();

    this.modalMovimentacao = this.root.querySelector('#modal-movimentacao') as ErpMovimentacaoFormModal;
    if (this._estoqueApi) this.modalMovimentacao.estoqueApi = this._estoqueApi;
    this.modalMovimentacao.aoSalvar = () => void this.carregar();

    this.root.querySelector('#botao-novo-insumo')?.addEventListener('pg-click', () => this.modalInsumo.abrirParaCriar());
    this.root.querySelector('#botao-novo-fornecedor')?.addEventListener('pg-click', () => this.modalFornecedor.abrir());
    this.root.querySelector('#botao-movimentacao')?.addEventListener('pg-click', () => {
      const fornecedores = this.resultadoFornecedores.status === 'ok' ? this.resultadoFornecedores.dado : [];
      this.modalMovimentacao.abrir(this.insumosAtuais, fornecedores);
    });

    this.root.querySelector('#insumos')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const insumo = this.insumosAtuais[index];
      if (insumo) this.modalInsumo.abrirParaEditar(insumo);
    });

    this.atualizarInsumos();
    this.atualizarFornecedores();
  }

  private async carregar(): Promise<void> {
    const api = this._estoqueApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoInsumos = { status: 'sem-permissao' };
      this.resultadoFornecedores = { status: 'sem-permissao' };
      this.resultadoMovimentacoes = { status: 'sem-permissao' };
      this.atualizarInsumos();
      this.atualizarFornecedores();
      this.atualizarMovimentacoes();
      return;
    }

    this.resultadoInsumos = { status: 'carregando' };
    this.resultadoFornecedores = { status: 'carregando' };
    this.resultadoMovimentacoes = { status: 'carregando' };
    this.atualizarInsumos();
    this.atualizarFornecedores();
    this.atualizarMovimentacoes();

    this.resultadoInsumos = await api.listarInsumos();
    this.atualizarInsumos();

    this.resultadoFornecedores = await api.listarFornecedores();
    this.atualizarFornecedores();

    this.resultadoMovimentacoes = await api.listarMovimentacoes({ limit: 30 });
    this.atualizarMovimentacoes();
  }

  private atualizarInsumos(): void {
    const container = this.root.querySelector('#insumos');
    if (!container) return;

    if (this.resultadoInsumos.status === 'ok') {
      this.insumosAtuais = this.resultadoInsumos.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS_INSUMOS;
      tabela.rows = this.insumosAtuais.map((i) => ({
        nome: i.nome,
        quantidade: `${i.quantidadeAtual} ${i.unidade}`,
        pontoReposicao: `${i.pontoReposicao} ${i.unidade}`,
        status: i.abaixoDoPontoDeReposicao
          ? { texto: 'Baixo estoque', tom: 'alerta' as const }
          : { texto: 'OK', tom: 'sucesso' as const },
      }));
      this.atualizarKpis(this.insumosAtuais);
      return;
    }

    this.insumosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoInsumos, 'Insumos', 'nenhum insumo cadastrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-baixo', 'kpi-zerado']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoInsumos.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  private atualizarKpis(insumos: Insumo[]): void {
    const total = insumos.length;
    const abaixo = insumos.filter((i) => i.abaixoDoPontoDeReposicao).length;
    const zerado = insumos.filter((i) => i.quantidadeAtual === 0).length;

    const kpiTotal = this.root.querySelector('#kpi-total') as PgKpiCard | null;
    const kpiBaixo = this.root.querySelector('#kpi-baixo') as PgKpiCard | null;
    const kpiZerado = this.root.querySelector('#kpi-zerado') as PgKpiCard | null;
    kpiTotal?.setAttribute('estado', 'ok');
    kpiTotal?.setAttribute('valor', String(total));
    kpiBaixo?.setAttribute('estado', 'ok');
    kpiBaixo?.setAttribute('valor', String(abaixo));
    kpiZerado?.setAttribute('estado', 'ok');
    kpiZerado?.setAttribute('valor', String(zerado));
  }

  private atualizarMovimentacoes(): void {
    const container = this.root.querySelector('#movimentacoes');
    if (!container) return;

    if (this.resultadoMovimentacoes.status === 'ok') {
      const nomePorInsumoId = new Map(this.insumosAtuais.map((i) => [i.id, i.nome]));
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS_MOVIMENTACOES;
      tabela.rows = this.resultadoMovimentacoes.dado.map((m) => ({
        insumo: nomePorInsumoId.get(m.insumoId) ?? m.insumoId.slice(0, 8) + '…',
        tipo: { texto: m.tipo === 'entrada' ? 'Entrada' : 'Saída', tom: m.tipo === 'entrada' ? ('sucesso' as const) : ('neutro' as const) },
        quantidade: String(m.quantidade),
        descricao: m.descricao ?? '—',
        criadoEm: new Date(m.criadoEm).toLocaleString('pt-BR'),
      }));
      return;
    }

    const { variant, titulo, descricao } = estadoVazio(this.resultadoMovimentacoes, 'Movimentações', 'nenhuma movimentação registrada ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarFornecedores(): void {
    const container = this.root.querySelector('#fornecedores');
    if (!container) return;

    if (this.resultadoFornecedores.status === 'ok') {
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS_FORNECEDORES;
      tabela.rows = this.resultadoFornecedores.dado.map((f) => ({
        nome: f.nome,
        telefone: f.telefone ?? '—',
        email: f.email ?? '—',
      }));
      return;
    }

    const { variant, titulo, descricao } = estadoVazio(this.resultadoFornecedores, 'Fornecedores', 'nenhum fornecedor cadastrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum registro', descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-estoque-page', ErpEstoquePage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-estoque-page': ErpEstoquePage;
  }
}
